import { useState, type FormEvent } from "react";
import { Alert, Button, InputText, SectionCard } from "@/deps/ui-library";
import { useCompanyProfile, useRunSetup, useSaveCompany, useSetupStatus } from "@/api/queries";
import type { CompanyProfile } from "@/api/types";
import { HttpError } from "@/api/http";
import { useTaxWords } from "@/app/session";

const EMPTY: CompanyProfile = {
  legalName: "",
  tradeName: "",
  pan: "",
  vatRegistered: false,
  address: { street: "", municipality: "", ward: "", district: "", province: "", postalCode: "" },
};

/**
 * The company's own details. They are copied onto every document it enters, so this is where a new
 * organisation starts: a 404 from the API means nobody has set it up yet, not that something broke.
 */
export function CompanyTab() {
  const { data, error, isPending } = useCompanyProfile();
  const notSetUp = error instanceof HttpError && error.problem.status === 404;

  if (isPending) return <p className="text-fg-muted text-sm">Loading</p>;
  if (error && !notSetUp) {
    return (
      <Alert
        config={{ variant: "subtle", colorScheme: "danger" }}
        description={(error as Error).message}
      />
    );
  }
  return (
    <div className="flex flex-col gap-4">
      {/* Not keyed on the name: saving a new one would rebuild the form and lose its "Saved." */}
      <CompanyForm initial={data ?? EMPTY} isNew={notSetUp} />
      {notSetUp ? null : <SetupBooks />}
    </div>
  );
}

const STEPS = [
  ["localisation", "Tax codes and rates"],
  ["chart", "Chart of accounts"],
  ["fiscalYear", "Current fiscal year, its periods and number series"],
] as const;

/**
 * What a new organisation needs before it can post. Asking again finishes whatever a refusal
 * interrupted, so the button stays until everything is ready.
 */
function SetupBooks() {
  const { data } = useSetupStatus();
  const run = useRunSetup();
  if (!data || STEPS.every(([key]) => data[key])) return null;

  return (
    <SectionCard title="Set up your books">
      <ul className="mb-3 flex flex-col gap-1 text-sm">
        {STEPS.map(([key, label]) => (
          <li key={key} className="text-foreground">
            {data[key] ? "Ready" : "Not set up"} · {label}
          </li>
        ))}
      </ul>
      <p className="text-fg-hint mb-3 text-xs">
        The tax pack is configuration supplied by the product owner, not tax or legal advice.
        Confirm the rates with your accountant before relying on them.
      </p>
      <Button disabled={run.isPending} onClick={() => run.mutate()}>
        {run.isPending ? "Setting up" : "Set up books"}
      </Button>
      {run.isError ? (
        <p role="alert" className="text-bad-text mt-2 text-sm">
          {(run.error as Error).message}
        </p>
      ) : null}
    </SectionCard>
  );
}

function CompanyForm({ initial, isNew }: { initial: CompanyProfile; isNew: boolean }) {
  const say = useTaxWords();
  const [profile, setProfile] = useState(initial);
  const save = useSaveCompany();
  const address = (field: keyof CompanyProfile["address"], value: string) =>
    setProfile({ ...profile, address: { ...profile.address, [field]: value } });

  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate(profile);
  }

  return (
    <form onSubmit={submit} className="grid gap-4 md:grid-cols-2">
      <SectionCard title="Company">
        <div className="flex flex-col gap-3">
          {isNew && (
            <Alert
              config={{ variant: "subtle", colorScheme: "info" }}
              description="Enter your company's details to start using the books."
            />
          )}
          <InputText
            label="Legal name"
            required
            value={profile.legalName}
            onChange={(e) => setProfile({ ...profile, legalName: e.target.value })}
          />
          <InputText
            label="Trading name"
            value={profile.tradeName}
            onChange={(e) => setProfile({ ...profile, tradeName: e.target.value })}
          />
          <InputText
            label={say("PAN")}
            className="num"
            value={profile.pan}
            onChange={(e) => setProfile({ ...profile, pan: e.target.value })}
          />
          <InputText
            label="Phone"
            value={profile.contactPhone ?? ""}
            onChange={(e) => setProfile({ ...profile, contactPhone: e.target.value })}
          />
          <InputText
            label="Email"
            type="email"
            value={profile.contactEmail ?? ""}
            onChange={(e) => setProfile({ ...profile, contactEmail: e.target.value })}
          />
          <label className="text-foreground flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={profile.vatRegistered}
              onChange={(e) => setProfile({ ...profile, vatRegistered: e.target.checked })}
            />
            {say("Registered for VAT")}
          </label>
        </div>
      </SectionCard>

      <SectionCard title="Registered address">
        <div className="flex flex-col gap-3">
          <InputText
            label="Street"
            value={profile.address.street}
            onChange={(e) => address("street", e.target.value)}
          />
          <InputText
            label="Ward or area"
            value={profile.address.ward}
            onChange={(e) => address("ward", e.target.value)}
          />
          <InputText
            label="Municipality"
            value={profile.address.municipality}
            onChange={(e) => address("municipality", e.target.value)}
          />
          <InputText
            label="Province"
            value={profile.address.province}
            onChange={(e) => address("province", e.target.value)}
          />
          <InputText
            label="Postal code"
            value={profile.address.postalCode}
            onChange={(e) => address("postalCode", e.target.value)}
          />
        </div>
      </SectionCard>

      <div className="flex items-center gap-3 md:col-span-2">
        <Button type="submit" disabled={save.isPending}>
          Save company details
        </Button>
        {save.isSuccess && <span className="text-fg-muted text-sm">Saved.</span>}
        {save.isError && (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(save.error as Error).message}
          />
        )}
      </div>
    </form>
  );
}
